import { useTranslation } from "react-i18next";
import { Globe, Users, Shield, Heart, Target, Lightbulb, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import MarketingHeader from "@/components/layout/MarketingHeader";
import { useNavigate } from "react-router-dom";
import { SEO } from "@/components/seo/SEO";

const About = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const values = [
    {
      icon: Globe,
      title: t("about.values.freedom.title", "Work Without Borders"),
      description: t("about.values.freedom.description", "We believe talent should be able to work from anywhere, without the bureaucratic nightmares."),
    },
    {
      icon: Shield,
      title: t("about.values.compliance.title", "Compliance First"),
      description: t("about.values.compliance.description", "We make regulatory complexity invisible so companies can focus on what matters: their people."),
    },
    {
      icon: Heart,
      title: t("about.values.employee.title", "Employee-Centric"),
      description: t("about.values.employee.description", "Happy employees create thriving businesses. Our platform puts the employee experience at the center."),
    },
    {
      icon: Lightbulb,
      title: t("about.values.innovation.title", "Continuous Innovation"),
      description: t("about.values.innovation.description", "The future of work is evolving. We stay ahead to ensure you're always compliant and competitive."),
    },
  ];

  const stats = [
    { value: "50+", label: t("about.stats.countries", "Countries covered") },
    { value: "10k+", label: t("about.stats.employees", "Employees managed") },
    { value: "99.9%", label: t("about.stats.uptime", "Platform uptime") },
    { value: "24/7", label: t("about.stats.support", "Support available") },
  ];

  const team = [
    {
      name: "Marie Laurent",
      role: t("about.team.roles.ceo", "CEO & Co-founder"),
      bio: t("about.team.marie.bio", "Former HR Director at a Fortune 500 company. Passionate about making global work accessible."),
    },
    {
      name: "Thomas Weber",
      role: t("about.team.roles.cto", "CTO & Co-founder"),
      bio: t("about.team.thomas.bio", "15 years in enterprise software. Built compliance tools used by governments worldwide."),
    },
    {
      name: "Sofia Rodriguez",
      role: t("about.team.roles.cpo", "Chief Product Officer"),
      bio: t("about.team.sofia.bio", "UX leader focused on making complex workflows simple. Previously at leading SaaS companies."),
    },
    {
      name: "James Chen",
      role: t("about.team.roles.compliance", "Head of Compliance"),
      bio: t("about.team.james.bio", "International tax attorney with expertise in cross-border employment regulations."),
    },
  ];

  return (
    <>
      <SEO 
        title={t("about.seo.title", "About Us - Renest")}
        description={t("about.seo.description", "Learn about Renest's mission to simplify global remote work compliance for modern companies.")}
      />
      <div className="min-h-screen bg-background">
        <MarketingHeader />

        {/* Hero Section */}
        <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="outline" className="mb-4">
              <Target className="h-3 w-3 mr-1" />
              {t("about.badge", "Our Mission")}
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
              {t("about.hero.title", "Making global remote work simple, compliant, and human")}
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t("about.hero.subtitle", "We started Renest because we saw too many companies struggle with the complexity of managing employees working abroad. Our mission is to remove those barriers.")}
            </p>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 border-y bg-muted/30">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl font-bold text-primary mb-2">{stat.value}</div>
                  <div className="text-muted-foreground">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Story Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-6">
                  {t("about.story.title", "Our Story")}
                </h2>
                <div className="space-y-4 text-muted-foreground">
                  <p>
                    {t("about.story.p1", "Renest was born in 2023 from a simple observation: the future of work is flexible, but the infrastructure to support it is stuck in the past.")}
                  </p>
                  <p>
                    {t("about.story.p2", "Our founders experienced firsthand the chaos of managing remote teams across borders—the tax complications, visa requirements, and compliance nightmares that kept HR teams up at night.")}
                  </p>
                  <p>
                    {t("about.story.p3", "We built Renest to be the platform we wished existed. One that handles complexity behind the scenes while giving employees the freedom they deserve and employers the peace of mind they need.")}
                  </p>
                </div>
              </div>
              <div className="relative">
                <div className="aspect-square bg-gradient-to-br from-primary/20 to-primary/5 rounded-2xl flex items-center justify-center">
                  <Globe className="h-32 w-32 text-primary/40" />
                </div>
                <div className="absolute -bottom-4 -right-4 bg-card border rounded-lg p-4 shadow-lg">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <Users className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <div className="font-semibold">{t("about.story.card.title", "Growing Team")}</div>
                      <div className="text-sm text-muted-foreground">{t("about.story.card.subtitle", "50+ passionate people")}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">
                {t("about.values.title", "What We Stand For")}
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                {t("about.values.subtitle", "Our values guide every decision we make and every feature we build.")}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => (
                <Card key={index} className="text-center">
                  <CardContent className="pt-6">
                    <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <value.icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold mb-2">{value.title}</h3>
                    <p className="text-sm text-muted-foreground">{value.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">
                {t("about.team.title", "Meet the Leadership")}
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                {t("about.team.subtitle", "A diverse team of HR experts, engineers, and compliance specialists.")}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {team.map((member, index) => (
                <Card key={index}>
                  <CardContent className="pt-6">
                    <div className="h-20 w-20 bg-muted rounded-full mx-auto mb-4 flex items-center justify-center">
                      <span className="text-2xl font-semibold text-muted-foreground">
                        {member.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <h3 className="font-semibold text-center">{member.name}</h3>
                    <p className="text-sm text-primary text-center mb-3">{member.role}</p>
                    <p className="text-sm text-muted-foreground text-center">{member.bio}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-primary text-primary-foreground">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">
              {t("about.cta.title", "Ready to simplify remote work?")}
            </h2>
            <p className="text-primary-foreground/80 mb-8 max-w-2xl mx-auto">
              {t("about.cta.subtitle", "Join hundreds of companies already using Renest to manage their global workforce with confidence.")}
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button 
                size="lg" 
                variant="secondary"
                onClick={() => navigate('/company-registration')}
              >
                {t("about.cta.getStarted", "Get Started Free")}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="bg-transparent border-primary-foreground/30 hover:bg-primary-foreground/10"
                onClick={() => navigate('/contact')}
              >
                {t("about.cta.talkToUs", "Talk to Us")}
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default About;
